/**
 * Portfolio Scroll-Cockpit (C2.3, oberer Teil):
 *  1. Kumulierte KPI-Karten (NKM/EBTDA/JÜ) — Prognose 2026 mit Δ% ggü. VJ 2025.
 *     RoE wird NICHT kumuliert (nicht additiv).
 *  2. Klickbare Gesellschafts-Tabelle (JÜ-Prog | JÜ-VJ | RoE-Prog | Netto-CF |
 *     FCF | FCFE). R2-Punkt 4: FCF/FCFE aufgenommen, „Ampel"/„Hinweis"-Spalten
 *     entfernt (FCF/FCFE sind keine Soll/Ist-Ampel-Größen). Klick selektiert auf
 *     DERSELBEN Seite (kein Router-Wechsel).
 *  3. R2-Punkt 1: Portfolio-Gesamtübersicht der Bestände (Bank/Kaution/Capex).
 *  4. Detail-Sektion der gewählten Gesellschaft (Drilldown-Inhalt C2.4).
 *
 * HARTE Regeln: null/fehlend → "—", NIE 0 (SCHEMA_MAPPING §5). Übrige Ampeln
 * (Asset-Detail, BWA) bleiben unverändert — nur die Übersichts-Ampel entfällt.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { ForbiddenError, UnauthorizedError } from '../api/errors';
import type { Entity, Meta, Portfolio } from '../types/snapshot';
import { KpiCard } from '../components/KpiCard';
import { AssetDetail } from '../components/AssetDetail';
import { PortfolioBestaende } from '../components/PortfolioBestaende';
import { NichtPortfolioTabelle } from '../components/NichtPortfolioTabelle';
import { AmortisationFussnote, AmortisationKachel } from '../components/AmortisationKachel';
import { KiFindings } from '../components/KiAnalyse';
import { computeAbweichung, formatEur, formatPct, formatValue } from '../lib/format';
import { useJahre } from '../lib/jahre';

const DASH = '—';

/** Fehler → Meldung; 401 → SSO-Login (Redirect), 403 → klare „Kein Zugriff"-Meldung
 *  (KEIN Login-Loop, ADR-003 v2). Returns null, wenn navigiert wurde. */
function mapAuthError(e: unknown, navigate: (p: string) => void): string | null {
  if (e instanceof UnauthorizedError) {
    navigate('/login');
    return null;
  }
  if (e instanceof ForbiddenError) {
    return 'Kein Zugriff — bitte den Administrator um Freischaltung bitten.';
  }
  return (e as Error)?.message ?? 'Fehler beim Laden';
}

function deltaFooter(prognose: number | null, vorjahr: number | null, vjJahr: number) {
  const d = computeAbweichung(prognose, vorjahr);
  return <>ggü. VJ {vjJahr}: {d === null ? DASH : formatPct(d)}</>;
}

export function PortfolioPage() {
  // D-089: Jahreszahlen der Beschriftungen aus meta (Layout → JahreProvider).
  const { bj, vj } = useJahre();
  const [data, setData] = useState<Portfolio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null); // slug
  const [detail, setDetail] = useState<Entity | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  // A-2: Bezugsstichtag der Legende. Aus meta.data_stand, NICHT hartkodiert —
  // ein fester Stichtag im Code veraltet mit dem naechsten Snapshot still.
  // Faellt der Abruf aus, entfaellt der Stichtag-Satz; die uebrige Legende bleibt.
  const [meta, setMeta] = useState<Meta | null>(null);
  const detailRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    api
      .getPortfolio()
      .then((p) => active && setData(p))
      .catch((e) => {
        if (!active) return;
        const m = mapAuthError(e, navigate);
        if (m) setError(m);
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  // A-2: Meta nur fuer den Bezugsstichtag der Legende. Fehler bleiben still —
  // ein fehlender Stichtag darf die Portfolio-Ansicht nicht blockieren.
  useEffect(() => {
    let active = true;
    api
      .getMeta()
      .then((m) => active && setMeta(m))
      .catch(() => active && setMeta(null));
    return () => {
      active = false;
    };
  }, []);

  // Drilldown der gewaehlten Gesellschaft (ein Rendering-Pfad: AssetDetail).
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetail(null);
    setDetailError(null);
    api
      .getAsset(selected)
      .then((e) => active && setDetail(e))
      .catch((e) => {
        if (!active) return;
        const m = mapAuthError(e, navigate);
        if (m) setDetailError(m);
      });
    return () => {
      active = false;
    };
  }, [selected, navigate]);

  // A-1 (Sprungmarke): nach der Auswahl zum eingebetteten Detail scrollen. Der
  // Sprung haengt an `selected`, NICHT an `detail` — die Section erscheint sofort
  // (mit „Lade Detail …"), der Klick reagiert dadurch unmittelbar, und die
  // Oberkante des Sprungziels bleibt beim Nachladen stabil. Der Gesellschaftsname
  // ist das erste Element im AssetDetail und steht damit oben, darunter die Daten.
  // jsdom kennt scrollIntoView nicht → optionaler Aufruf statt Crash im Test.
  useEffect(() => {
    if (!selected) return;
    detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [selected]);

  if (error) {
    return (
      <p role="alert" className="text-ampel-red">
        {error}
      </p>
    );
  }
  if (!data) {
    return <p className="text-text-muted">Lade Portfolio …</p>;
  }

  const cum = data.cumulative;

  return (
    <div className="space-y-8">
      {/* 1) Kumulierte KPI-Karten — Prognose 2026 vs VJ 2025 */}
      <section>
        <h1 className="mb-4 text-xl font-semibold text-text">
          Portfolio — Prognose {bj}
        </h1>
        {/* D-084: vierte Kachel (Amortisation) → 2 Spalten ab sm, 4 ab lg. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            label="Nettokaltmiete (NKM)"
            value={cum.jahr_prognose.nkm}
            footer={deltaFooter(cum.jahr_prognose.nkm, cum.vorjahr.nkm, vj)}
          />
          <KpiCard
            label="EBTDA"
            value={cum.jahr_prognose.ebtda}
            footer={deltaFooter(cum.jahr_prognose.ebtda, cum.vorjahr.ebtda, vj)}
          />
          <KpiCard
            label="Jahresüberschuss (JÜ)"
            value={cum.jahr_prognose.ju}
            footer={deltaFooter(cum.jahr_prognose.ju, cum.vorjahr.ju, vj)}
          />
          {/* D-084: Σ über pph["members"] (Backend), Curare/GE nie enthalten (I10). */}
          <AmortisationKachel amortisation={data.amortisation_kumuliert} />
        </div>
        <AmortisationFussnote amortisation={data.amortisation_kumuliert} />
      </section>

      {/* D-084: KI-Findings unter den Summary-Kacheln — nur Portfolio-Mitglieder
          (Backend filtert auf pph["members"]); ohne Feld keine Sektion. */}
      <KiFindings eintraege={data.ki_summary} />

      {/* 2) Klickbare Gesellschafts-Tabelle */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-text">Gesellschaften</h2>
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th scope="col" className="px-4 py-2 font-medium">Gesellschaft</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">JÜ-Prognose</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">JÜ-VJ {vj}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">RoE-Prognose</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Netto-CF{' '}
                  <span aria-hidden="true" className="font-normal text-text-muted" title="Vorläufig – siehe Fußnote (D-030)">‡</span>
                </th>
                {/* R2-Punkt 4: FCF/FCFE aufgenommen; „Ampel"/„Hinweis" bewusst entfernt
                    (FCF/FCFE sind keine Soll/Ist-Ampel-Größen). Übrige Ampeln unberührt. */}
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  FCF{' '}
                  <span aria-hidden="true" className="font-normal text-text-muted" title="Free Cashflow VOR Tilgung – siehe Fußnote (D-030)">‡</span>
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  FCFE{' '}
                  <span aria-hidden="true" className="font-normal text-text-muted" title="Vorläufig – enthält die Tilgung (D-030)">‡</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => {
                const isSel = selected === m.slug;
                return (
                  <tr
                    key={m.short_name}
                    role="button"
                    tabIndex={0}
                    aria-label={`Gesellschaft ${m.short_name} anzeigen`}
                    onClick={() => setSelected(m.slug)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelected(m.slug);
                      }
                    }}
                    className={`cursor-pointer border-b border-border last:border-0 hover:bg-surface-2 ${
                      isSel ? 'bg-surface-2' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-text">{m.short_name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatEur(m.ju_prognose)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{formatEur(m.ju_vj)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatValue(m.roe_prognose, { kind: 'percent' })}
                    </td>
                    <td
                      className={`px-4 py-3 text-right tabular-nums ${
                        typeof m.netto_cf_prognose === 'number' && m.netto_cf_prognose < 0
                          ? 'text-ampel-red'
                          : ''
                      }`}
                    >
                      {formatEur(m.netto_cf_prognose)}
                    </td>
                    {/* R2-Punkt 4: FCF/FCFE-Jahresprognose. Negativ rot (analog Netto-CF). */}
                    <td
                      className={`px-4 py-3 text-right tabular-nums ${
                        typeof m.fcf_prognose === 'number' && m.fcf_prognose < 0 ? 'text-ampel-red' : ''
                      }`}
                    >
                      {formatEur(m.fcf_prognose)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right tabular-nums ${
                        typeof m.fcfe_prognose === 'number' && m.fcfe_prognose < 0 ? 'text-ampel-red' : ''
                      }`}
                    >
                      {formatEur(m.fcfe_prognose)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* A-2: Legende zu allen drei gekennzeichneten Spalten. Die bestehende
            ‡-Aussage (FCF vor Tilgung; Netto-CF/FCFE enthalten Tilgung, D-030)
            bleibt wortgleich erhalten und wird um Bezugsstichtag und
            Hochrechnungssystematik ergänzt — nicht ersetzt. */}
        <p className="mt-2 text-xs text-text-muted">
          ‡ Netto-CF, FCF und FCFE sind Jahres-Prognosen {bj}
          {meta?.data_stand ? ` auf Basis ${meta.data_stand}` : ''}. Hochrechnung nach
          R4-Mix: abgeschlossene Quartale als Ist, laufendes Quartal als Ist-Monate
          zzgl. Hochrechnung, künftige Quartale zum Soll — kein annualisiertes
          Einzelquartal. FCF ist Free Cashflow vor Tilgung (enthält keine Tilgung).
          Netto-CF und FCFE-Prognose sind vorläufig: sie enthalten die Tilgung
          (Jahres-Tilgung nach R4-Mix, gegen Darlehensspiegel noch ungeprüft – D-030).
        </p>
      </section>

      {/* R2-Punkt 1: Portfolio-Gesamtübersicht der Bestände je Gesellschaft */}
      <PortfolioBestaende members={data.members} />

      {/* D-075 „Punkt D": Curare/Gewerbeenergien SEPARAT — nicht im Portfolio-
          Aggregat. Steht bewusst UNTER den Portfolio-Blöcken und außerhalb der
          Gesellschafts-Tabelle: `entities` ist seit D-075 nicht mehr das
          Portfolio, und die KPI-Karten oben summieren ausschließlich über
          pph["members"].
          D-084 (Aufgabe 3): die Zeilen teilen die Auswahl mit der Gesellschafts-
          Tabelle → derselbe Detail-Drilldown unten (AssetDetail, ein Pfad). Die
          Auswahl berührt keine Portfolio-Größe (Kacheln/Tabelle lesen nur `data`). */}
      <NichtPortfolioTabelle
        nonMembers={data.non_members}
        selected={selected}
        onSelect={setSelected}
      />

      {/* 4) Detail-Drilldown der gewählten Gesellschaft (eingebettet, ein Pfad) */}
      {selected ? (
        <section
          ref={detailRef}
          aria-label={`Detail ${selected}`}
          className="scroll-mt-4 border-t border-border pt-6"
        >
          {detailError ? (
            <p role="alert" className="text-ampel-red">
              {detailError}
            </p>
          ) : detail ? (
            <AssetDetail entity={detail} />
          ) : (
            <p className="text-text-muted">Lade Detail …</p>
          )}
        </section>
      ) : (
        <p className="text-text-muted">Eine Gesellschaft auswählen, um Details zu sehen.</p>
      )}
    </div>
  );
}
